import classNames from 'classnames';
import React, { useCallback, useMemo, JSX } from 'react';
import { cleanBoxProps } from '../../../utils/cleanBoxProps';
import { IBoxComponent, IBoxSelectComponent } from '@enuan/chat__utils';
import { FormRow } from './FormRow';
import { xor as _xor } from 'lodash';
import { styled } from '@mui/material/styles';

export const Select = styled(
	({
		className,
		label,
		error,
		error_label,
		help_label,
		required_label,
		options,
		value,
		onChange,
		multiple,
		..._props
	}: Omit<JSX.IntrinsicElements['select'], 'onChange'> &
		IBoxComponent<IBoxSelectComponent> & {
			onChange?: (value: string[]) => void;
		}) => {
		const props = cleanBoxProps(_props);

		const values = useMemo(
			() => (value ? (Array.isArray(value) ? value : [value]) : []),
			[value],
		);

		const _onChange = useCallback(
			(_newValues: string[]) => {
				const newValues = _xor(values, _newValues);
				onChange?.(newValues);
			},
			[values, onChange],
		);

		return (
			<FormRow
				className={classNames('form__row-select', className)}
				label={label}
				error={error}
				error_label={error_label}
				help_label={help_label}
				required_label={required_label}
			>
				<select
					value={multiple ? values : (values[0] ?? '')}
					onChange={({ currentTarget: { value } }) => {
						const newValues = Array.isArray(value) ? value : [value];
						_onChange(newValues);
					}}
					className="select input"
					{...props}
				>
					<option value="">--</option>
					{options.map(({ label, value }, index) => (
						<option value={value} key={index}>
							{label}
						</option>
					))}
				</select>
			</FormRow>
		);
	},
	{
		label: 'SelectHTML',
		target: 'SelectHTML-root',
	},
)({
	'& .select': {
		'& .select__placeholder': {
			color: 'var(--form__select__placeholder_color)',
		},
		'& .select__control': {
			padding: '0 !important',
			cursor: 'pointer',
			borderBottom: 0,
			border: 0,
			minHeight: '2.4em',
			'& .select__value-container': {
				padding: '0 0.5em',
			},
			'&.select__control--is-focused, &:focus': {
				borderColor: 'transparent',
				boxShadow: 'none',
				borderRadius: '0.6em 0.6em 0 0',
				'& .select__dropdown-indicator': {
					transform: 'rotate(180deg)',
				},
			},
		},
		'& .select__indicator-separator': {
			display: 'none',
		},
		'& .select__indicator': {
			color: 'var(--form__select__indicator_color)',
			padding: '0.5em 0.6em',
			'&:hover': {
				color: 'var(--form__select__indicator--hover_color)',
			},
			'& svg': {
				width: '1.3em',
				height: '1.5em',
				display: 'inline-block',
				fill: 'currentColor',
				lineHeight: 1,
				stroke: 'currentColor',
				strokeWidth: 0,
			},
		},
		'& .select__menu': {
			margin: 0,
			boxShadow: 'none',
			padding: 0,
			border: 0,
			left: 0,
			backgroundColor: 'transparent',
			'& .select__menu-list': {
				margin: 0,
				padding: 0,
				border: 0,
				marginTop: '-1px',
			},
			'& .select__option': {
				cursor: 'pointer',
				fontSize: '1.5em',
				fontFamily: 'Roboto, sans-serif',
				fontStyle: 'italic',
				fontWeight: 400,
				paddingLeft: '0.5em',
				paddingRight: '0.5em',
				backgroundColor: 'var(--form__select__option_background)',
				color: 'var(--form__select__option_color)',
				display: 'flex',
				alignItems: 'center',
				minHeight: '2.4em',
				transition: 'all 150ms',
				'&:hover': {
					backgroundColor: 'var(--form__select__option--hover_background)',
					color: 'var(--form__select__option--hover_color)',
				},
				'&.select__menu-list--is-selected': {
					color: 'var(--form__select__option--selected_color)',
					backgroundColor: 'var(--form__select__option--selected_background)',
				},
				'&:last-child': {
					borderRadius: '0 0 0.6em 0.6em',
					borderBottom: '1px solid var(--form__select__option--last_border)',
				},
			},
		},
		'&.select--error .select__placeholder': {
			color: 'var(--form__select--error__placeholder_color)',
		},
	},
});
